import compact from 'lodash/compact';
import max from 'lodash/max';
import { colors as akColors } from '@atlaskit/theme';
import { getTokenValue } from '@atlaskit/tokens';
import { NON_WORKING } from '../../../common/constants';
import type { BurnupChartData, WorkRateRange } from '../../../common/types';
import { calculateIdealWorkRate } from '../../../common/utils/ideal-work-rate-guideline';
import { type SprintEvent, getScope } from '../../../common/utils/sprint-event';

export const isNonWorkingRange = (range: WorkRateRange): boolean => range.rate === NON_WORKING;

type MarkAreaPoint = {
	xAxis: string;
};

export const workRateRangeToEChartsMarkArea = (
	range: WorkRateRange,
): [MarkAreaPoint, MarkAreaPoint] => [{ xAxis: range.start }, { xAxis: range.end }];

/**
 * The sprint is overtime if its planned end has past and the real end date has more than
 * 1 day difference with the planned end.
 */
export const isSprintOvertime = (plannedEndTime: Date, now: Date, maxDate: Date): boolean =>
	plannedEndTime.getTime() < now.getTime() &&
	maxDate.getTime() - plannedEndTime.getTime() > 1000 * 60 * 60 * 24;

/**
 * Returns the latest point in the chart.
 */
export const getLastKnownSprintDate = (burnup: BurnupChartData, now: Date): Date => {
	const knownEndTimes = compact([
		// If the sprint has ended then its end is a candidate. Otherwise the current time is used.
		burnup.sprintEndEvent ? new Date(burnup.sprintEndEvent.timestamp).getTime() : now.getTime(),
		// The last event in the series is a candidate to the edge boundary
		burnup.scopeChangeEvents[burnup.scopeChangeEvents.length - 1] &&
			new Date(burnup.scopeChangeEvents[burnup.scopeChangeEvents.length - 1].timestamp).getTime(),
		// The planned end time is what should be shown while the sprint is on track
		burnup.endTime && new Date(burnup.endTime).getTime(),
	]);

	// @ts-expect-error - TS2769 - No overload matches this call.
	return new Date(max(knownEndTimes));
};

type Guideline = {
	silent: true;
	type: 'line';
	symbol: 'none';
	lineStyle: {
		color: string;
	};
	z: number;
	data: [string, number][];
	markLine: {
		z: number;
		lineStyle: {
			color: string;
			type: string;
		};
		symbol: string;
		label: {
			color: string;
			formatter: string;
		};
		data: {
			xAxis: number;
		}[];
	} | null;
};

/**
 * Generate ECharts series for the ideal work rate guideline
 */
const getGuideline = (
	burnup: BurnupChartData,
	now: Date,
	maxDate: Date,
	sprintEvents: SprintEvent[],
): Guideline => {
	const plannedEndTime = new Date(burnup.endTime);

	// The end date for the guideline the planned time if the sprint hasn't ended and isn't overtime
	const endDate = getLastKnownSprintDate(burnup, now);

	const guideline = calculateIdealWorkRate({
		startDate: new Date(burnup.sprintStartEvent.timestamp),
		scope: sprintEvents.length ? getScope(sprintEvents[sprintEvents.length - 1]) : 0,
		endDate,
	});

	const plannedEndLine = isSprintOvertime(plannedEndTime, now, maxDate)
		? {
				z: 1,
				lineStyle: {
					color: akColors.N50A,
					type: 'solid',
				},
				symbol: 'none',
				label: { color: akColors.N100, formatter: 'Planned end' },
				data: [{ xAxis: new Date(burnup.endTime).getTime() }],
			}
		: null;

	return {
		silent: true,
		type: 'line',
		symbol: 'none',
		lineStyle: {
			color: getTokenValue('color.chart.neutral', akColors.N60),
		},
		z: 1,
		data: guideline,
		// We render a line at the planned end if the sprint went overtime
		markLine: plannedEndLine,
	};
};

export default getGuideline;
